/**
 * @file Frontend `Admin` — typed Client for the admin console SPA.
 *       Class is intentionally not exposed via the package's exports
 *       map; only `main.tsx` constructs it (relative import).
 * @module @labcraft/admin/src/client
 *
 * The workspace exports exactly one Client class per the rule in
 * `.agents/rules/services.md` § "Workspace Client shape — one
 * `CoreClient`, lifecycle namespacers": `Admin extends CoreClient`,
 * with one `CoreLifecycle` namespacer per UI panel (`admin.learners`
 * ↔ `/learners`, `admin.emails` ↔ `/emails`, `admin.settings` ↔
 * `/settings`, `admin.secrets` ↔ `/secrets`). Shell/chrome concerns
 * (`me`, `signout`) live as flat methods on the parent — they're not
 * panels, so they're not namespaced.
 *
 * `main.tsx` owns the 401-bounce wiring: it calls
 * `http.on('unauthorized', ...)` with a callback that flips the `qkMe`
 * TanStack entry to `{ admin: null }`, then constructs `Admin` with
 * the same transport. This Client doesn't depend on `@tanstack/react-
 * query` — it's vanilla wire surface, SW-ready.
 *
 * Shares its class name with the backend `Admin` in
 * `../service/index.ts` by design — the two never appear in the same
 * module, per ADR-010's package-name-as-class-name convention.
 */

import { CoreClient, type CoreTransport } from '@labcraft/core'
import type {
  HttpClientTransport,
  WsClientTransport,
} from '@labcraft/core/frontend'
import { Courses } from './courses.ts'
import { Emails } from './emails.ts'
import { Hypervisor } from './hypervisor.ts'
import { Learners } from './learners.ts'
import { Recalibration } from './recalibration.ts'
import { Secrets } from './secrets.ts'
import { Settings } from './settings.ts'
import {
  MeRoute,
  SignoutRoute,
  type MeResponse,
  type SignoutResponse,
} from '../shared/routes.ts'

/**
 * Structural transport contract every admin sub-namespacer (and the
 * parent) consumes — the `request` + `stream` + `on('unauthorized', ...)`
 * subset of `HttpClientTransport`, intersected with `CoreTransport` so
 * the parent can carry it as a `CoreClient` transport-record value
 * (whose constraint is `Record<string, CoreTransport>`).
 *
 * Typed as a `Pick<>` rather than a hand-rolled interface so the
 * production class stays the single source of truth. Both
 * `HttpClientTransport` and `VirtualHostTransport` extend `CoreTransport`
 * and expose the same three methods, so the integration harness
 * constructs `Admin` with `VirtualHostTransport` without test-side type
 * acrobatics.
 */
export type HttpTransport = CoreTransport &
  Pick<HttpClientTransport, 'request' | 'stream'>

/**
 * Structural WebSocket transport contract — the `subscribe` surface of
 * `WsClientTransport`, intersected with `CoreTransport` so the parent
 * can carry it as a `CoreClient` transport-record value. Consumed by
 * the `Hypervisor` namespacer's read-only terminal observer. Mirrors
 * lab's `WsTransport` seam so the integration harness swaps a virtual
 * transport in without test-side type acrobatics.
 */
export type WsTransport = CoreTransport & Pick<WsClientTransport, 'subscribe'>

////////////////////////////////////////////////////////////////////////////////
// Props + Deps
////////////////////////////////////////////////////////////////////////////////

/**
 * Constructor props for the frontend `Admin`. No knobs beyond the
 * lifecycle id; transport-level configuration (baseUrl, timeouts)
 * lives on the `HttpClientTransport` the caller passes via `deps`.
 *
 * @property id - CoreLifecycle id (default `'admin'`).
 */
export type AdminProps = {
  id?: string
}

/**
 * Runtime dependencies. The operator (`main.tsx` for production
 * mounts; the integration harness for tests) constructs the transport
 * once and threads it through. Per-namespacer construction picks the
 * same instance up by reference.
 *
 * @property http - Typed HTTP transport. Stateless; one instance per
 *   page load. `Admin`'s `CoreClient` base `adopt`s it so the
 *   lifecycle child-walk drives configure/start/stop.
 * @property ws - Typed WebSocket transport for the read-only terminal
 *   observer. Adopted alongside `http`; one instance per page load.
 */
export type AdminDeps = {
  http: HttpTransport
  ws: WsTransport
}

////////////////////////////////////////////////////////////////////////////////
// Admin (frontend Client)
////////////////////////////////////////////////////////////////////////////////

/**
 * Top-level frontend Client for `admin.labcraft.dev`. Single
 * `CoreClient` export of `@labcraft/admin/src/client`.
 *
 * Owns three panel-namespacers ({@link Learners}, {@link Settings},
 * {@link Secrets}), each a `CoreLifecycle` whose logs and metrics
 * bubble under `admin.<panel>.*`. The shell-level `me()` / `signout()`
 * methods live flat on the parent — they aren't panels, so they don't
 * deserve a namespace.
 *
 * The 401 → session-expired cache flip lives in `main.tsx`, not here.
 * This Client is React-free.
 */
export class Admin extends CoreClient<
  AdminProps,
  { http: HttpTransport; ws: WsTransport }
> {
  readonly learners: Learners
  readonly emails: Emails
  readonly courses: Courses
  readonly recalibration: Recalibration
  readonly settings: Settings
  readonly secrets: Secrets
  readonly hypervisor: Hypervisor

  constructor(props: AdminProps, deps: AdminDeps) {
    super(
      { id: props.id ?? 'admin' },
      { transports: { http: deps.http, ws: deps.ws } },
    )
    const http = this.deps.transports.http
    const ws = this.deps.transports.ws
    this.learners = this.adopt(new Learners(http))
    this.emails = this.adopt(new Emails(http))
    this.courses = this.adopt(new Courses(http))
    this.recalibration = this.adopt(new Recalibration(http))
    this.settings = this.adopt(new Settings(http))
    this.secrets = this.adopt(new Secrets(http))
    this.hypervisor = this.adopt(new Hypervisor(http, ws))
  }

  /**
   * `GET /api/account/me` — return the authenticated admin session,
   * or `{ admin: null }` for anonymous. Wrapped by `useMe()`.
   */
  async me(): Promise<MeResponse> {
    return this.deps.transports.http.request(MeRoute)
  }

  /**
   * `POST /api/account/signout` — drop the Redis session row and
   * clear the admin cookie. Wrapped by `useSignoutMutation()`; the
   * hook's `onSuccess` does the synchronous
   * `setQueryData(qkMe, { admin: null })` so the chrome bounces
   * before the next render.
   */
  async signout(): Promise<SignoutResponse> {
    return this.deps.transports.http.request(SignoutRoute)
  }
}
